/**
 * Citation System - Single Source of Truth
 *
 * This module handles all citation-related constants and utilities for the inline citation system.
 *
 * ## Architecture Overview
 *
 * Citations flow through the system as follows:
 *
 * ### 1. Streaming (Real-time)
 * ```
 * Backend sends:
 *   TextEvent("The answer is 42")     → fullContent += "The answer is 42"
 *   CitationEvent({cited_text: "..."}) → fullContent += "{{cite:1}}"
 *   TextEvent(". More text...")        → fullContent += ". More text..."
 *
 * Result: "The answer is 42{{cite:1}}. More text..."
 * ```
 *
 * ### 2. Storage (Firebase)
 * ```
 * Backend stores (NO markers in text):
 * {
 *   content: [
 *     { type: "text", text: "The answer is 42", citations: [{...}] },
 *     { type: "text", text: ". More text..." }
 *   ]
 * }
 * ```
 *
 * ### 3. History Loading
 * ```
 * Frontend converts Firebase → Frontend format:
 *   - For EACH text block: append markers for that block's citations
 *   - Result: Same as streaming output
 * ```
 *
 * ### 4. Rendering
 * ```
 * rehypeCitations plugin transforms:
 *   "{{cite:1}}" → <sup class="citation-marker">1</sup>
 * ```
 *
 * ## Key Rules
 *
 * 1. **Markers in text, NOT in content blocks during streaming**
 *    - Text blocks should NOT have citations array during streaming
 *    - Citations array is only at message level for tooltip lookups
 *
 * 2. **One marker insertion point per flow**
 *    - Streaming: `client.ts` adds markers when CitationEvent arrives
 *    - History: `message-converter.ts` adds markers per text block
 *
 * 3. **Never double-add markers**
 *    - If text already has markers (from streaming), don't add again
 *    - Use `textHasCitationMarkers()` to check
 *
 * @module constants/citations
 */

// =============================================================================
// MARKER CONSTANTS
// =============================================================================

/** Prefix used in citation markers */
export const CITATION_MARKER_PREFIX = "{{cite:" as const;

/** Suffix used in citation markers */
const CITATION_MARKER_SUFFIX = "}}" as const;

/** Regex to match citation markers like {{cite:1}}, {{cite:2}}, etc. */
export const CITATION_MARKER_REGEX = /\{\{cite:(\d+)\}\}/g;

// =============================================================================
// MARKER UTILITIES
// =============================================================================

/**
 * Generate a citation marker string.
 *
 * @param citationNumber - The citation number (1-indexed)
 * @returns Citation marker string (e.g., "{{cite:1}}")
 *
 * @example
 * createCitationMarker(1) // "{{cite:1}}"
 * createCitationMarker(5) // "{{cite:5}}"
 */
export const createCitationMarker = (citationNumber: number): string => {
  return `${CITATION_MARKER_PREFIX}${citationNumber}${CITATION_MARKER_SUFFIX}`;
};

// =============================================================================
// CONTENT BLOCK UTILITIES
// =============================================================================

/**
 * Result of processing content blocks for citations.
 * Uses generic type to support both Anthropic Citation and Firebase citation formats.
 */
export interface ProcessedCitationContent<T = unknown> {
  /** Combined text with citation markers inserted */
  text: string;
  /** All citations collected from blocks (for tooltip lookups) */
  citations: T[];
}

/**
 * Process content blocks and insert citation markers.
 *
 * This is the SINGLE SOURCE OF TRUTH for converting content blocks
 * with citations into text with inline markers.
 *
 * Used by:
 * - message-converter.ts (history loading)
 *
 * NOT used by:
 * - client.ts (streaming) - markers added incrementally as CitationEvents arrive
 *
 * @param blocks - Array of content blocks (text, thinking, etc.)
 * @returns Processed content with text and collected citations
 *
 * @example
 * const blocks = [
 *   { type: "text", text: "Answer is 42", citations: [{...}] },
 *   { type: "text", text: ". More info." }
 * ];
 * const result = processContentBlocksForCitations(blocks);
 * // result.text = "Answer is 42{{cite:1}}. More info."
 * // result.citations = [{...}]
 */
export function processContentBlocksForCitations<T = unknown>(
  blocks: unknown[]
): ProcessedCitationContent<T> {
  let textContent = "";
  const citations: T[] = [];

  for (const block of blocks) {
    // Type guard for text blocks
    if (
      typeof block === "object" &&
      block !== null &&
      "type" in block &&
      (block as { type: string }).type === "text" &&
      "text" in block
    ) {
      const textBlock = block as { type: "text"; text: string; citations?: T[] };
      let blockText = textBlock.text || "";

      // Append citation markers at the end of THIS block
      if (
        textBlock.citations &&
        Array.isArray(textBlock.citations) &&
        textBlock.citations.length > 0
      ) {
        for (const citation of textBlock.citations) {
          const citationIndex = citations.length + 1;
          citations.push(citation);
          blockText += createCitationMarker(citationIndex);
        }
      }

      textContent += blockText;
    }
  }

  return { text: textContent, citations };
}
